/**
 * Boundary transform for a backend grid-spec descriptor column.
 *
 * The single place that reads the raw `/grid-spec/` column shape (snake_case,
 * nullable) and turns it into the stable, defaulted, camelCase column the grid
 * renderer consumes. Every descriptor key the wire carries is absorbed here, so
 * the renderer never hand-destructures the descriptor again and a new backend
 * key flows through without touching the renderer.
 *
 * Contains:
 * - normalizeDescriptorColumn — descriptor column → normalized column
 * - buildPinnedColumns — descriptor pins, led by the selection checkbox
 */

import { NUMERIC_TYPES } from './columnSizing';

/**
 * Humanize a column `field` into a readable header for the case where the
 * descriptor's `label_key` has no i18n entry and no literal `label` was sent.
 * Splits snake_case and camelCase, drops a trailing `_id`, and Title-Cases the
 * words: `installed_units_count` → "Installed Units Count", `customerId` →
 * "Customer". Without this, an untranslated header renders the raw field name
 * (e.g. "is_virtual"), which is what surfaced on the new grids.
 *
 * @param {string} field
 * @returns {string}
 */
function humanizeField(field) {
  if (!field || typeof field !== 'string') return field ?? '';
  return field
    .replace(/_id$/, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(/[_\s]+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Normalize one descriptor column into the renderer's camelCase shape.
 *
 * Owns the two derivations the renderer used to reimplement per type: the
 * header source (`label_key` → field fallback) and the align default (explicit
 * non-left align wins; numeric types right-align; everything else inherits the
 * grid default). Sizing values are surfaced as-is — the renderer applies the
 * width-wins-over-flex precedence and the per-type min-width defaults.
 *
 * @param {object} specCol  One entry from the descriptor `columns` array.
 * @returns {{ field: string, type: string, headerLabel: string, headerFallback: string,
 *   sortable: boolean,
 *   filterable: boolean,
 *   width: (number|undefined), minWidth: (number|undefined), flex: (number|undefined),
 *   align: (string|undefined), options: (object[]|undefined), actions: (object[]|undefined),
 *   defaultHidden: boolean, editable: boolean, hideable: boolean, exportable: boolean,
 *   exportAlways: boolean, payloadOnly: boolean,
 *   defaultPinned: (string|null) }}
 */
export function normalizeDescriptorColumn(specCol) {
  const {
    field,
    type = 'string',
    label_key: labelKey,
    label,
    sortable,
    filterable,
    width,
    min_width: minWidth,
    flex,
    align,
    options,
    actions,
    format,
    default_hidden: defaultHidden,
    editable,
    hideable,
    export: exportRule,
    payload_only: payloadOnly,
    default_pinned: defaultPinned,
  } = specCol ?? {};

  // Header source: the descriptor's label_key, falling back to the field name.
  // Resolution through the translator stays in the renderer (it owns `t`).
  const headerLabel = labelKey ?? field;
  // Header text shown when label_key has no translation: the descriptor's
  // literal `label` (the label/label_key split), else a humanized field name.
  // The renderer passes this as the translator's default value, so a real i18n
  // entry still wins; only the untranslated case falls back to this.
  const headerFallback = label ?? humanizeField(field);

  // Align: an explicit non-left align wins; numeric types right-align; anything
  // else inherits the grid default (expressed as undefined). A literal 'left'
  // on a numeric column still falls to the numeric default — matches the prior
  // renderer behaviour.
  let resolvedAlign;
  if (align && align !== 'left') {
    resolvedAlign = align;
  } else if (NUMERIC_TYPES.has(type)) {
    resolvedAlign = 'right';
  }

  return {
    field,
    type,
    headerLabel,
    headerFallback,
    sortable: sortable !== false,
    // The backend always emits filterable; default closed for older descriptors.
    filterable: Boolean(filterable),
    width: width ?? undefined,
    minWidth: minWidth ?? undefined,
    flex: flex ?? undefined,
    align: resolvedAlign,
    options,
    actions,
    // Sub-format hint for datetime columns ("date" = date-only, no timezone).
    format: format ?? undefined,
    defaultHidden: Boolean(defaultHidden),
    editable: Boolean(editable),
    // hideable defaults true when the descriptor omits the key; only an
    // explicit `false` locks the column visible (non-toggleable).
    hideable: hideable !== false,
    // The backend marks a column non-exportable when its value carries no
    // meaning as text — an image URL, a row's action cluster. Absent key →
    // exportable, matching the descriptor's own default.
    exportable: exportRule !== false,
    // export: "always" — in every CSV export regardless of on-screen
    // visibility or toggles; the descriptor's position decides where.
    exportAlways: exportRule === 'always',
    // payload_only — the entry exists solely for the export path (the value
    // feeds a rich cell); the grid renders no screen column or toggle for it.
    payloadOnly: Boolean(payloadOnly),
    // default_pinned drives the grid's initial pinned-columns state. Null means
    // unpinned.
    defaultPinned: defaultPinned ?? null,
  };
}

/**
 * Build the initial pinned-column model from the descriptor's own pin hints.
 * The backend is the only source of which columns pin and on which side; a
 * grid never hand-lists its pins.
 *
 * @param {object[]} specColumns  The descriptor's raw `columns` array.
 * @param {?string} selectionField  Field id of the grid's generated selection
 *   checkbox, or null when the grid has no selection column. The checkbox is
 *   minted by the grid at render time, so no descriptor column can declare it;
 *   it leads the left group whenever the descriptor pins anything there, since
 *   a checkbox that scrolls away from the row it selects is never wanted. With
 *   nothing pinned left it stays unpinned — alone it would anchor nothing.
 * @returns {{ left: string[], right: string[] }|null}
 */
export function buildPinnedColumns(specColumns, selectionField = null) {
  const left = [];
  const right = [];
  for (const column of specColumns ?? []) {
    const { field, defaultPinned, payloadOnly } = normalizeDescriptorColumn(column);
    if (payloadOnly) continue;
    if (defaultPinned === 'left') left.push(field);
    if (defaultPinned === 'right') right.push(field);
  }

  if (selectionField && left.length) {
    left.unshift(selectionField);
  }

  return left.length || right.length ? { left, right } : null;
}
